import React from 'react';

import BoldVerticalBarChart, {
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import RadialBarChart from '@components/report/chart/radial-bar-chart/RadialBarChart';
import { RadialBarChartProps } from '@components/report/chart/radial-bar-chart/RadialBarChart';
import VideoContentItem, {
	VideoContentItemProps,
} from '@components/report/content-items/video-content-item/VideoContentItem';
import NoDataSection from '@components/report/no-data-section/NoDataSection';
import ReportCard from '@components/report/report-card/ReportCard';
import SnapCarousel from '@shared/snap-carousel/SnapCarousel';
import {
	lackOfPermissionClass,
	threeDivisionGridClass,
} from '@components/report/style.css';

import { containerClass } from './style.css';
import { useTranslation } from 'react-i18next';

export interface AdContentProps {
	adContentRate?: RadialBarChartProps;
	averageAdContentViews?: BoldVerticalBarChartProps;
	averageAdContentEngagement?: BoldVerticalBarChartProps;
	items?: VideoContentItemProps[];

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const AdContent = ({
	adContentRate,
	averageAdContentViews,
	averageAdContentEngagement,
	isLoading,
	isError,
	lackOfPermission,
	...props
}: AdContentProps) => {
	const { t } = useTranslation('infReportYoutube');
	const status = { isLoading, isError, lackOfPermission };
	const available = !isLoading && !isError && !lackOfPermission;
	const items =
		isLoading || isError || lackOfPermission
			? new Array(20).fill({} as VideoContentItemProps)
			: props.items ?? [];

	if (!available || (available && items.length > 0))
		return (
			<div className={containerClass}>
				<div className={threeDivisionGridClass}>
					<ReportCard
						label={t(
							'features.report.section-content.yt.ad-content.ad-content-rate.label',
						)}
						tooltip={t(
							'features.report.section-content.yt.ad-content.ad-content-rate.tooltip',
						)}
						lackOfPermission={lackOfPermission}
					>
						<RadialBarChart
							title={t(
								'features.report.section-content.yt.ad-content.ad-content-rate.radial-bar-chart.title',
							)}
							legends={[
								{
									color: 'primary',
									label: t(
										'features.report.section-content.yt.ad-content.ad-content-rate.radial-bar-chart.ad-content',
									),
									isMain: true,
								},
								{
									color: 'gray',
									label: t(
										'features.report.section-content.yt.ad-content.ad-content-rate.radial-bar-chart.recent-content',
									),
								},
							]}
							{...status}
							{...adContentRate}
						/>
					</ReportCard>
					<ReportCard
						label={t(
							'features.report.section-content.yt.ad-content.average-ad-content-views.label',
						)}
						tooltip={t(
							'features.report.section-content.yt.ad-content.average-ad-content-views.tooltip',
						)}
						lackOfPermission={lackOfPermission}
					>
						<BoldVerticalBarChart
							desc={true}
							data={[{ isMain: true }, {}]}
							{...status}
							{...averageAdContentViews}
						/>
					</ReportCard>
					<ReportCard
						label={t(
							'features.report.section-content.yt.ad-content.average-ad-content-engagement.label',
						)}
						tooltip={t(
							'features.report.section-content.yt.ad-content.average-ad-content-engagement.tooltip',
						)}
						lackOfPermission={lackOfPermission}
					>
						<BoldVerticalBarChart
							desc={true}
							data={[{ isMain: true }, {}]}
							{...status}
							{...averageAdContentEngagement}
						/>
					</ReportCard>
				</div>
				<SnapCarousel
					className={lackOfPermission ? lackOfPermissionClass : ''}
					items={items?.map((item, idx) => (
						<VideoContentItem key={idx} idx={idx} {...item} {...status} />
					))}
				/>
			</div>
		);
	else return <NoDataSection />;
};

export default AdContent;
